import type { BaseBreakPointConfigProtocol, DesignStyle, InteractionStyleType, VariableADTvalue } from '@lighthouse/core'
import { useMemo } from 'react'

import { useAppContainerContext, useAppThemeContext } from '../../components'
import type { ThemeDesignInitConfig } from '../../constants'
import { themeBlockDesignConfig } from '../../constants'
import { getBackgroundStyle, getBorderStyle, getRadiusStyle, getShadowStyle, getTextColorStyle } from '../../utils/design'

type GetFilterInteractionStylesOptions = {
    parseVariableImage?: (v: VariableADTvalue) => string
}

export type FilterInteractionStyles = Record<'filter' | 'icon', Record<InteractionStyleType, React.CSSProperties>>

export type FilterInteractionStylesKeys = Record<'filter' | 'icon', (keyof DesignStyle)[]>

export type FilterStyles = Record<'filter' | 'icon', React.CSSProperties & { placeholderColor?: string }>

export const useFilterInteractionStyles = (
    breakPoint?: BaseBreakPointConfigProtocol,
    options?: GetFilterInteractionStylesOptions
): FilterInteractionStyles | undefined => {
    const { appId } = useAppContainerContext()
    const { theme } = useAppThemeContext()
    const parseVariableImage = options?.parseVariableImage
    const { palettes } = theme
    const breakPointDesign = breakPoint?.design

    const { hoverStyle, activeStyle, disabledStyle } = useMemo(() => {
        const styleTheme = breakPointDesign?.theme || 'LIGHT'
        if (styleTheme === 'CUSTOM') {
            return {
                hoverStyle: breakPoint?.hoverStyle,
                activeStyle: breakPoint?.activeStyle,
                disabledStyle: breakPoint?.disabledStyle
            }
        }
        return themeBlockDesignConfig('filter', theme)[styleTheme] as ThemeDesignInitConfig
    }, [breakPointDesign?.theme, theme, breakPoint?.hoverStyle, breakPoint?.activeStyle, breakPoint?.disabledStyle])

    return useMemo(() => {
        if (!hoverStyle && !activeStyle && !disabledStyle) {
            return undefined
        }

        return {
            filter: {
                hoverStyle: hoverStyle?.open
                    ? {
                          ...getTextColorStyle(hoverStyle?.design?.color, palettes),
                          ...getRadiusStyle(hoverStyle?.design?.radius),
                          ...getBackgroundStyle({ appId, value: hoverStyle?.design?.background, palettes, parseVariableImage }),
                          ...getBorderStyle(hoverStyle?.design?.border, palettes),
                          ...getShadowStyle(hoverStyle?.design?.shadow, palettes)
                      }
                    : {},
                activeStyle: activeStyle?.open
                    ? {
                          ...getTextColorStyle(activeStyle?.design?.color, palettes),
                          ...getRadiusStyle(activeStyle?.design?.radius),
                          ...getBackgroundStyle({ appId, value: activeStyle?.design?.background, palettes, parseVariableImage }),
                          ...getBorderStyle(activeStyle?.design?.border, palettes),
                          ...getShadowStyle(activeStyle?.design?.shadow, palettes)
                      }
                    : {},
                disabledStyle: disabledStyle?.open
                    ? {
                          ...getTextColorStyle(disabledStyle?.design?.color, palettes),
                          ...getRadiusStyle(disabledStyle?.design?.radius),
                          ...getBackgroundStyle({ appId, value: disabledStyle?.design?.background, palettes, parseVariableImage }),
                          ...getBorderStyle(disabledStyle?.design?.border, palettes),
                          ...getShadowStyle(disabledStyle?.design?.shadow, palettes)
                      }
                    : {}
            },
            icon: {
                hoverStyle: hoverStyle?.open
                    ? {
                          ...getTextColorStyle(hoverStyle?.design?.iconColor, palettes)
                      }
                    : {},
                activeStyle: activeStyle?.open
                    ? {
                          ...getTextColorStyle(activeStyle?.design?.iconColor, palettes)
                      }
                    : {},
                disabledStyle: disabledStyle?.open
                    ? {
                          ...getTextColorStyle(disabledStyle?.design?.iconColor, palettes)
                      }
                    : {}
            }
        }
    }, [activeStyle, appId, disabledStyle, hoverStyle, palettes, parseVariableImage])
}

export const useFilterStyles = (
    breakPoint?: BaseBreakPointConfigProtocol,
    options?: GetFilterInteractionStylesOptions
): FilterStyles | undefined => {
    const { appId } = useAppContainerContext()
    const { theme } = useAppThemeContext()
    const parseVariableImage = options?.parseVariableImage
    const { palettes } = theme
    const breakPointDesign = breakPoint?.design

    const design = useMemo(() => {
        const styleTheme = breakPointDesign?.theme || 'LIGHT'
        if (styleTheme === 'CUSTOM') {
            return breakPointDesign
        }
        return themeBlockDesignConfig('filter', theme)[styleTheme]?.design
    }, [breakPointDesign, theme])

    return useMemo(() => {
        if (!design) {
            return undefined
        }
        return {
            filter: {
                ...getTextColorStyle(design?.color, palettes),
                ...getRadiusStyle(design?.radius),
                ...getBackgroundStyle({ appId, value: design?.background, palettes, parseVariableImage }),
                ...getBorderStyle(design?.border, palettes),
                ...getShadowStyle(design?.shadow, palettes)
            },
            icon: {
                ...getTextColorStyle(design?.iconColor, palettes)
            }
        }
    }, [appId, design, palettes, parseVariableImage])
}

//  // 标题颜色
//     labelColor?: BackgroundNoImageProtocol

//     // 文本颜色
//     color?: BackgroundNoImageProtocol

//     // 图标颜色
//     iconColor?: BackgroundNoImageProtocol

//     // 占位符颜色
//     placeholderColor?: BackgroundNoImageProtocol

//     // 圆角
//     radius?: EdgeValue

//     // 背景
//     background?: BackgroundProtocol
//     // 文本边框
//     // textBorder?: Border

//     // 纹理
//     veins?: Veins

//     // 边框
//     border?: Border

//     // 阴影
//     shadow?: Shadow

//     // 模糊
//     blur?: number
